import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Generate year options (current year to +3 years)
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 4 }, (_, i) => currentYear + i);

// Month names per language
const monthNames: Record<string, string[]> = {
  uz: [
    'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
    'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr',
  ],
  ru: [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
  ],
};

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate();
}

export default function ContactSection() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('ru') ? 'ru' : 'uz';

  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [privacyError, setPrivacyError] = useState(false);

  // Custom date state
  const today = new Date();
  const [selYear, setSelYear] = useState<string>('');
  const [selMonth, setSelMonth] = useState<string>('');
  const [selDay, setSelDay] = useState<string>('');

  const daysInMonth = selYear && selMonth ? getDaysInMonth(Number(selYear), Number(selMonth)) : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Build ISO date string for form submission
  const visitDateValue =
    selYear && selMonth && selDay
      ? `${selYear}-${String(selMonth).padStart(2, '0')}-${String(selDay).padStart(2, '0')}`
      : '';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!privacyChecked) {
      setPrivacyError(true);
      return;
    }
    setPrivacyError(false);

    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setFormStatus('sending');
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });
    // Append custom date
    if (visitDateValue) data.set('visit_date', visitDateValue);

    try {
      const res = await fetch('https://readdy.ai/api/form/d6q27udsbgrc47filv10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
        setPrivacyChecked(false);
        setSelYear('');
        setSelMonth('');
        setSelDay('');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const placeholderYear = lang === 'ru' ? 'Год' : 'Yil';
  const placeholderMonth = lang === 'ru' ? 'Месяц' : 'Oy';
  const placeholderDay = lang === 'ru' ? 'День' : 'Kun';

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 to-teal-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full uppercase tracking-wider mb-3">
            {t('contact_badge')}
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('contact_title')}</h2>
          <p className="text-gray-500 text-sm max-w-xl mx-auto">{t('contact_subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Contact Info */}
          <div className="space-y-6">
            {/* Uzbekistan Office */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg mr-3">
                  <i className="ri-map-pin-2-line text-teal-600 text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-gray-900">{t('contact_office_uz')}</h3>
              </div>
              <div className="space-y-3">
                <a href="tel:+998931121464" className="flex items-center text-sm text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                  <i className="ri-phone-line text-teal-500 mr-2"></i>
                  +998 93 112 14 64
                </a>
                <div className="flex items-center space-x-3 pl-6">
                  <a href="https://t.me/+998931121464" target="_blank" rel="nofollow noopener noreferrer" className="flex items-center text-xs text-teal-600 hover:text-teal-700 transition-colors cursor-pointer">
                    <i className="ri-telegram-line mr-1"></i> Telegram
                  </a>
                  <a href="https://wa.me/998931121464" target="_blank" rel="nofollow noopener noreferrer" className="flex items-center text-xs text-teal-600 hover:text-teal-700 transition-colors cursor-pointer">
                    <i className="ri-whatsapp-line mr-1"></i> WhatsApp
                  </a>
                </div>
              </div>
            </div>

            {/* Korea Office */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-lg mr-3">
                  <i className="ri-hospital-line text-rose-500 text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-gray-900">{t('contact_office_kr')}</h3>
              </div>
              <div className="space-y-3">
                <a href="tel:+821028758562" className="flex items-center text-sm text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                  <i className="ri-phone-line text-teal-500 mr-2"></i>
                  +82 10-2875-8562
                </a>
                <div className="flex items-center space-x-3 pl-6">
                  <a href="https://t.me/+821028758562" target="_blank" rel="nofollow noopener noreferrer" className="flex items-center text-xs text-teal-600 hover:text-teal-700 transition-colors cursor-pointer">
                    <i className="ri-telegram-line mr-1"></i> Telegram
                  </a>
                  <a href="https://wa.me/821028758562" target="_blank" rel="nofollow noopener noreferrer" className="flex items-center text-xs text-teal-600 hover:text-teal-700 transition-colors cursor-pointer">
                    <i className="ri-whatsapp-line mr-1"></i> WhatsApp
                  </a>
                  <a href="https://open.kakao.com/o/medivoyage" target="_blank" rel="nofollow noopener noreferrer" className="flex items-center text-xs text-yellow-600 hover:text-yellow-700 transition-colors cursor-pointer">
                    <i className="ri-kakao-talk-line mr-1"></i> KakaoTalk
                  </a>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg mr-3">
                  <i className="ri-mail-send-line text-teal-600 text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-gray-900">{t('contact_email_label')}</h3>
              </div>
              <a href="mailto:medivoyage.uz@gmail.com" className="text-sm text-teal-600 hover:text-teal-700 transition-colors cursor-pointer">
                medivoyage.uz@gmail.com
              </a>
            </div>

            {/* Social */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-500 rounded-xl p-6 text-white">
              <p className="text-sm font-semibold mb-3">{t('contact_social_title')}</p>
              <div className="flex space-x-3">
                <a href="https://t.me/medivoyage" target="_blank" rel="nofollow noopener noreferrer" className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-telegram-line text-base"></i>
                </a>
                <a href="https://wa.me/998931121464" target="_blank" rel="nofollow noopener noreferrer" className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-whatsapp-line text-base"></i>
                </a>
                <a href="#" target="_blank" rel="nofollow noopener noreferrer" className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-base"></i>
                </a>
                <a href="#" target="_blank" rel="nofollow noopener noreferrer" className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-base"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h3 className="text-lg font-bold text-gray-900 mb-6">{t('contact_form_title')}</h3>

            {formStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">{t('contact_success_title')}</p>
                <p className="text-sm text-gray-500">{t('contact_success_msg')}</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-6 px-5 py-2 text-sm font-medium bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('contact_send_another')}
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('full_name')}</label>
                    <input type="text" name="name" required className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('phone')}</label>
                    <input type="tel" name="phone" required className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('email')}</label>
                  <input type="email" name="email" required className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('contact_service_label')}</label>
                  <select name="service" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none cursor-pointer">
                    <option value="">{t('contact_service_placeholder')}</option>
                    <option value="consultation">{t('contact_service_consultation')}</option>
                    <option value="dental">{t('service_dental')}</option>
                    <option value="plastic">{t('service_plastic')}</option>
                    <option value="antiaging">{t('service_antiaging')}</option>
                    <option value="orthopedic">{t('service_orthopedic')}</option>
                    <option value="eye">{t('service_eye')}</option>
                    <option value="skin">{t('service_skin')}</option>
                    <option value="cancer">{t('service_cancer')}</option>
                    <option value="spine">{t('service_spine')}</option>
                    <option value="gynecology">{t('service_gynecology')}</option>
                    <option value="proctology">{t('service_proctology')}</option>
                    <option value="checkup">{t('service_checkup')}</option>
                    <option value="visa">{t('contact_service_visa')}</option>
                    <option value="accommodation">{t('contact_service_accommodation')}</option>
                    <option value="interpreter">{t('contact_service_interpreter')}</option>
                    <option value="tour">{t('contact_service_tour')}</option>
                    <option value="package">{t('contact_service_package')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('contact_specialty_label')}</label>
                  <select name="specialty" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none cursor-pointer">
                    <option value="">{t('contact_specialty_placeholder')}</option>
                    <option value="dental">{t('contact_specialty_dental')}</option>
                    <option value="plastic">{t('contact_specialty_plastic')}</option>
                    <option value="eye">{t('contact_specialty_eye')}</option>
                    <option value="skin">{t('contact_specialty_skin')}</option>
                    <option value="orthopedic">{t('contact_specialty_orthopedic')}</option>
                    <option value="spine">{t('contact_specialty_spine')}</option>
                    <option value="cancer">{t('contact_specialty_cancer')}</option>
                    <option value="gynecology">{t('contact_specialty_gynecology')}</option>
                    <option value="antiaging">{t('contact_specialty_antiaging')}</option>
                    <option value="checkup">{t('contact_specialty_checkup')}</option>
                    <option value="proctology">{t('contact_specialty_proctology')}</option>
                    <option value="other">{t('contact_specialty_other')}</option>
                  </select>
                </div>

                {/* Custom Date Picker */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('contact_visit_date_label')}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {/* Year */}
                    <select
                      value={selYear}
                      onChange={(e) => { setSelYear(e.target.value); setSelDay(''); }}
                      className="w-full px-2 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none cursor-pointer"
                    >
                      <option value="">{placeholderYear}</option>
                      {years.map((y) => (
                        <option key={y} value={String(y)}>{y}</option>
                      ))}
                    </select>
                    {/* Month */}
                    <select
                      value={selMonth}
                      onChange={(e) => { setSelMonth(e.target.value); setSelDay(''); }}
                      className="w-full px-2 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none cursor-pointer"
                    >
                      <option value="">{placeholderMonth}</option>
                      {monthNames[lang].map((name, idx) => {
                        const monthNum = idx + 1;
                        const isDisabled =
                          selYear === String(today.getFullYear()) &&
                          monthNum < today.getMonth() + 1;
                        return (
                          <option key={monthNum} value={String(monthNum)} disabled={isDisabled}>
                            {name}
                          </option>
                        );
                      })}
                    </select>
                    {/* Day */}
                    <select
                      value={selDay}
                      onChange={(e) => setSelDay(e.target.value)}
                      className="w-full px-2 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none cursor-pointer"
                    >
                      <option value="">{placeholderDay}</option>
                      {days.map((d) => {
                        const isDisabled =
                          selYear === String(today.getFullYear()) &&
                          selMonth === String(today.getMonth() + 1) &&
                          d < today.getDate();
                        return (
                          <option key={d} value={String(d)} disabled={isDisabled}>
                            {d}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{t('contact_visit_date_hint')}</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('contact_message_label')}</label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                  />
                  <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-rose-500' : 'text-gray-400'}`}>
                    {charCount}/500
                  </p>
                </div>

                {/* Privacy Consent */}
                <div className={`flex items-start gap-2.5 p-3 rounded-md border ${privacyError ? 'border-red-300 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    id="contact-privacy-consent"
                    checked={privacyChecked}
                    onChange={(e) => {
                      setPrivacyChecked(e.target.checked);
                      if (e.target.checked) setPrivacyError(false);
                    }}
                    className="mt-0.5 w-4 h-4 accent-teal-600 cursor-pointer flex-shrink-0"
                  />
                  <label htmlFor="contact-privacy-consent" className="text-xs text-gray-600 leading-relaxed cursor-pointer">
                    {t('privacy_consent')} <span className="text-red-500">*</span>
                  </label>
                </div>
                {privacyError && (
                  <p className="text-xs text-red-500 -mt-2">{t('privacy_consent_required')}</p>
                )}

                {formStatus === 'error' && (
                  <p className="text-xs text-rose-500">{t('contact_error_msg')}</p>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'sending' || charCount > 500}
                  className="w-full py-3 text-sm font-semibold bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'sending' ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      {t('contact_sending')}
                    </span>
                  ) : (
                    t('contact_submit')
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
